import { useState, useRef, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { useEffectOnce } from "../../../../hooks/useEffectOnce";
import useProcess from "../../../../hooks/useProcess";
import { config } from "../../../../Environment";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Label } from "../../../../components/ui/label";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import {
  ShoppingCart,
  Play,
  Coins,
} from "lucide-react";

function displayDateFromTimestamp(ts: string) {
  // moment(activityLastUpdatedDate).fromNow() always shows in English, need to localize if we will use it
  const d = new Date(ts);
  const year = d.getFullYear();
  const yearText = String(year).padStart(4, "0");
  const month = d.getMonth();
  const monthText = String(month).padStart(2, "0");
  const dayOfMonth = d.getDate();
  const dayOfMonthText = String(dayOfMonth).padStart(2, "0");
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(dayOfMonth)
  ) {
    return null;
  }
  return `${yearText}-${monthText}-${dayOfMonthText}`;
}

function BuyServiceModal({ productInfo, activityMetadata, authorInfo, buyServiceModalVisible, setBuyServiceModalVisible }: any) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const retryFetch = useRetryFetch();
    const { startProcess, resumeProcess } = useProcess();

  const [runningProcess, setRunningProcess] = useState<any>(null);
  const [vendor, setVendor] = useState<any>(null);

  async function loadUserEngagement() {
    const query = new URLSearchParams();
    query.set('workflowId', productInfo.data.service.fiWorkflow);
    query.set('status', 'RUNNING');
    const response = await retryFetch(`${config.API_BASE}/api/user/process/search?${query.toString()}`);
    const result = await response.json();
    if (Array.isArray(result.list) && result.list.length > 0) {
      const found = result.list[0];
      console.log(`loadUserEngagement found ${JSON.stringify(found)}`);
      setRunningProcess(found);
    }
    setBuyServiceModalVisible(true);
  }

  /*
  async function loadVendor() {
    const query = new URLSearchParams();
    query.set('vendorId', productInfo.fiVendor);
    const response = await retryFetch(`${config.API_BASE}/api/user/vendor/search?${query.toString()}`);
    const result = await response.json();
    if (Array.isArray(result.list) && result.list.length > 0) {
      const found = result.list[0];
      setVendor(found);
    } else {
      setBuyServiceModalVisible(false);
    }
  }
    */

    useEffectOnce(() => {
      loadUserEngagement();
      // loadVendor();
    }, []);
  
  async function resumeServiceActivity() {
    await resumeProcess({
      processId: runningProcess._id,
      from: location,
      forward: location,
      onError: () => {
        toast.error("An error occurred while resuming the activity.");
      },
    });
  }

  async function startServiceActivity() {
    await startProcess({
      prescriptionId: productInfo.data.service.fiWorkflow,
      from: location,
      forward: location,
      onError: () => {
        toast.error("An error occurred while starting the activity.");
      },
    });
  }

  function authorLetters() {
    if (activityMetadata?.vendor?.displayName) {
      return activityMetadata?.vendor?.displayName.split(" ")[0];
    }
    if (activityMetadata?.fiUser?.email) {
      return activityMetadata?.fiUser?.email.split("@")[0];
    }
    return "?";
  }

  function authorName() {
    if (activityMetadata?.vendor?.displayName) {
      return (<span>{activityMetadata.vendor.displayName}</span>)
    } else if (activityMetadata?.fiUser?.email) {
      return (<span>{activityMetadata.fiUser.email}</span>)
    } else {
      return (<span>Unknown</span>)
    }
  }

  function vendorPageLink() {
    if (activityMetadata?.vendor?._id) {
      return (
        <Link to={`/marketplace/vendor/${activityMetadata.vendor._id}`} >Learn more</Link>
      );
    } else {
      return (<span></span>)
    }
  }

  function renderServiceInProgress() {
    if (!runningProcess) {
      return (<></>);
    }
    return (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t("status")}</h3>
              <p>You already started an engagement for this service. You can resume it or start a new engagement.</p>
              <Button
                size="sm"
                onClick={() => {
                  resumeServiceActivity();
                }}
              >
                <Play className="h-4 w-4 mr-1" />
                {t("resume")}
              </Button>
          </div>
    );
  }

  function renderBuyService() {
    return (
      <div className="space-y-3">
          <p>Start a new engagement for this service.</p>
          <Button
            size="sm"
            onClick={() => {
              startServiceActivity();
            }}
          >
            <Play className="h-4 w-4 mr-1" />
            {t("start")}
          </Button>
      </div>
    );
  }


    return (
      <Dialog
        open={buyServiceModalVisible}
        onOpenChange={setBuyServiceModalVisible}
      >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("product_type_service")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
            <div className="space-y-3">
                <h3 className="text-sm font-medium">{t("name")}</h3>
                <p>{productInfo.name}</p>
            </div>
          {/* Author Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t("label_created_by")}</h3>
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full overflow-hidden">
                <img
                  src={`https://ui-avatars.com/api/?name=${authorLetters()}`}
                  alt="Avatar"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium">{authorName()}</h4>
                  {/* <Button variant="ghost" size="sm" className="h-6 px-2">
                    <ExternalLink className="h-3 w-3" />
                  </Button> */}
                  <p>{vendorPageLink()}</p>
                </div>
                {/* <p className="text-sm text-muted-foreground">
                  {mockAuthor.bio}
                </p> */}
              </div>
            </div>
          </div>

          {renderServiceInProgress()}
          {renderBuyService()}

        </div>
      </DialogContent>
        </Dialog>
    );
}

export default BuyServiceModal;